/**
 * product-visualizer.js
 * Motor de composicao visual do Product Visualizer — Pop Dot.
 *
 * Depende de (carregados antes via defer):
 *   window.PopdotVisualEngine      (popdot-visual-engine.js)
 *   window.ProductVisualizerConfig (product-visualizer-config.js)
 *   window.ProductVisualizerData   (exposto pelo .liquid)
 *
 * Nao interfere em: galeria nativa, variant-picker, product-form, checkout.
 */

(function () {
  'use strict';

  // Atalhos para engine e config
  var E   = window.PopdotVisualEngine;
  var cfg = function () { return window.ProductVisualizerConfig || {}; };

  function log()  { if (cfg().debug) console.log.apply(console,  ['[PV]'].concat(Array.prototype.slice.call(arguments))); }
  function warn() { if (cfg().debug) console.warn.apply(console, ['[PV]'].concat(Array.prototype.slice.call(arguments))); }

  // ==========================================================================
  // CACHE DE OVERLAYS — preload em memória para troca instantânea
  // ==========================================================================

  var overlayCache = new Map();

  function preloadOverlay(src) {
    if (overlayCache.has(src)) return overlayCache.get(src);
    var promise = new Promise(function (resolve, reject) {
      var img    = new Image();
      img.onload  = function () { resolve(src); };
      img.onerror = function () { reject(src); };
      img.src = src;
    });
    overlayCache.set(src, promise);
    return promise;
  }

  function preloadOverlaysForOrientation(orientation, assetBaseUrl) {
    if (!orientation) return;
    var materials = ['paper', 'canvas'];
    var frames    = ['natural', 'freijo', 'imbuia', 'preto', 'branco'];
    var files     = [];

    materials.forEach(function (mat) {
      frames.forEach(function (frm) {
        var filename = E.resolveOverlay(orientation, mat, frm, cfg());
        if (!filename) return;
        var src = (assetBaseUrl || '') + filename;
        files.push(filename);
        preloadOverlay(src).catch(function () {
          // silencioso — arquivo pode nao existir para essa combinacao
        });
      });
    });

    log('preloading overlays', { orientation: orientation, count: files.length, files: files });
  }

  // ==========================================================================
  // LEITURA DO ESTADO INICIAL DA VARIANTE
  // ==========================================================================

  function readVariantOptions() {
    var selectors = cfg().variantPickerSelectors || ['variant-picker'];
    var picker    = null;

    for (var i = 0; i < selectors.length; i++) {
      picker = document.querySelector(selectors[i]);
      if (picker) break;
    }

    if (!picker) {
      warn('variant-picker nao encontrado no DOM');
      return { material: '', frame: '' };
    }

    // Fonte primaria: JSON embarcado no variant-picker
    var jsonScript = picker.querySelector('script[type="application/json"]');
    if (jsonScript) {
      try {
        var variant = JSON.parse(jsonScript.textContent || '{}');
        var options = variant.options || [];
        var fi      = cfg().optionIndex || { material: 1, frame: 2 };
        log('Variante inicial (JSON):', variant.title, '| options:', options);
        return {
          material: options[fi.material] || '',
          frame:    options[fi.frame]    || '',
        };
      } catch (e) {
        warn('Erro ao parsear JSON da variante inicial:', e);
      }
    }

    // Fallback DOM: inputs checked e selects
    var fi        = cfg().optionIndex || { material: 1, frame: 2 };
    var fieldsets = picker.querySelectorAll('fieldset[data-fieldset-index]');
    var optMap    = {};

    fieldsets.forEach(function (fs) {
      var idx     = parseInt(fs.dataset.fieldsetIndex, 10);
      var checked = fs.querySelector('input:checked');
      if (checked) optMap[idx] = checked.value;
    });

    picker.querySelectorAll('select').forEach(function (sel, i) {
      var opt = sel.options[sel.selectedIndex];
      if (opt) optMap[i] = opt.value;
    });

    log('Variante inicial (DOM fallback):', optMap);
    return {
      material: optMap[fi.material] || '',
      frame:    optMap[fi.frame]    || '',
    };
  }

  // ==========================================================================
  // APLICACAO DA COMPOSICAO
  // ==========================================================================

  function applyComposition(artworkEl, overlayEl, orientation, material, frame, assetBaseUrl) {
    var container = artworkEl.closest('.pv-artwork');
    if (!container) {
      warn('Container .pv-artwork nao encontrado');
      return;
    }

    var mat      = E.resolveMaterial(material, cfg());
    var frm      = E.resolveFrame(frame, cfg());
    var filename = E.resolveOverlay(orientation, mat, frm, cfg());

    // Atualiza data-attributes
    container.dataset.pvOrientation = orientation || '';
    container.dataset.pvMaterial    = mat         || '';
    container.dataset.pvFrame       = frm         || '';

    // Defensivo: remove overlays duplicados acumulados por versoes anteriores do bug
    var existingOverlays = container.querySelectorAll('.pv-overlay');
    if (existingOverlays.length > 1) {
      for (var i = 1; i < existingOverlays.length; i++) {
        existingOverlays[i].remove();
      }
    }

    log('applyComposition:', {
      material:    mat,
      frame:       frm,
      orientation: orientation,
      overlay:     filename,
      overlayNull: filename === null,
    });

    // Label de estado
    var labelEl = document.querySelector('.pv-label');
    if (labelEl) {
      var matLabel = mat === 'canvas' ? 'Canvas' : 'Papel Fotográfico';
      var frmLabel = (frm && frm !== 'none')
        ? frm.charAt(0).toUpperCase() + frm.slice(1)
        : 'Sem moldura';
      labelEl.textContent = matLabel + ' · ' + frmLabel;
    }

    if (!filename) {
      // Sem moldura — remove overlay imediatamente, sem loading
      container.classList.remove('pv-artwork--loading');
      container.classList.remove('pv-artwork--load-done');
      container.querySelectorAll('.pv-overlay').forEach(function (el) {
        el.style.display = 'none';
        el.src           = '';
      });
      return;
    }

    var newSrc = (assetBaseUrl || '') + filename;

    // SEMPRE busca o overlay já existente no container — nunca confia
    // apenas no parâmetro overlayEl, que pode estar desatualizado ou null
    // (ex: chamadas vindas do pointerdown antecipado passam null de propósito).
    var overlay = container.querySelector('.pv-overlay');
    if (!overlay) {
      overlay = document.createElement('img');
      overlay.className = 'pv-overlay';
      overlay.alt       = '';
      overlay.setAttribute('aria-hidden', 'true');
      container.appendChild(overlay);
    }

    if (overlay.src === newSrc) {
      overlay.style.display = '';
      return;
    }

    // Verifica cache
    var cached  = overlayCache.get(newSrc);
    var isReady = cached && (cached._state === 'resolved');

    if (isReady) {
      log('overlay from cache', newSrc);
      overlay.src           = newSrc;
      overlay.style.display = '';
    } else {
      container.classList.add('pv-artwork--loading');
      container.classList.remove('pv-artwork--load-done');

      if (overlay._pvLoad)  overlay.removeEventListener('load',  overlay._pvLoad);
      if (overlay._pvError) overlay.removeEventListener('error', overlay._pvError);

      overlay._pvLoad = function () {
        container.classList.remove('pv-artwork--loading');
        container.classList.add('pv-artwork--load-done');
        setTimeout(function () { container.classList.remove('pv-artwork--load-done'); }, 400);
        var p = overlayCache.get(newSrc);
        if (p) p._state = 'resolved';
        log('overlay carregado:', filename);
      };

      overlay._pvError = function () {
        container.classList.remove('pv-artwork--loading');
        warn('overlay nao encontrado:', newSrc);
      };

      overlay.addEventListener('load',  overlay._pvLoad);
      overlay.addEventListener('error', overlay._pvError);
      overlay.src = newSrc;
      preloadOverlay(newSrc);
    }

    overlay.style.display = '';
  }

  // ==========================================================================
  // INICIALIZACAO
  // ==========================================================================

  function init() {
    if (!E) {
      console.error('[PV] PopdotVisualEngine nao encontrado. Verifique a ordem de carregamento dos scripts.');
      return;
    }

    var container = document.querySelector('.pv-artwork');
    if (!container) return;

    log('Inicializando Product Visualizer');

    var artworkEl    = container.querySelector('.pv-base-art');
    var overlayEl    = container.querySelector('.pv-overlay');
    var pvData       = window.ProductVisualizerData || {};
    var assetBaseUrl = pvData.assetBaseUrl || '';
    var orientHint   = pvData.orientationHint || '';

    if (!artworkEl) {
      warn('.pv-base-art nao encontrado — abortando');
      return;
    }

    function update(materialRaw, frameRaw) {
      var orientation = E.resolveOrientation(artworkEl, orientHint, cfg());

      if (!orientation) {
        artworkEl.addEventListener('load', function onLoad() {
          artworkEl.removeEventListener('load', onLoad);
          orientation = E.resolveOrientation(artworkEl, orientHint, cfg());
          log('orientacao via metafield:', orientation);
          preloadOverlaysForOrientation(orientation, assetBaseUrl);
          applyComposition(artworkEl, overlayEl || container.querySelector('.pv-overlay'), orientation, materialRaw, frameRaw, assetBaseUrl);
          overlayEl = container.querySelector('.pv-overlay');
        });
        return;
      }

      log('orientacao via metafield:', orientation);
      preloadOverlaysForOrientation(orientation, assetBaseUrl);
      applyComposition(artworkEl, overlayEl || container.querySelector('.pv-overlay'), orientation, materialRaw, frameRaw, assetBaseUrl);
      overlayEl = container.querySelector('.pv-overlay');
    }

    var initial = readVariantOptions();
    log('Estado inicial:', initial);
    update(initial.material, initial.frame);

    // Listener principal: evento de troca de variante do tema (Tinker)
    var fallbackTimer = null;

    document.addEventListener('variant-update', function (event) {
      var variant = event && event.detail && event.detail.resource;
      if (!variant) { warn('variant-update sem detail.resource'); return; }

      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
        log('variant-update cancelou fallback pendente');
      }

      var options = variant.options || [];
      var fi      = cfg().optionIndex || { material: 1, frame: 2 };
      log('variant-update:', variant.title, '| options:', options);
      update(options[fi.material] || '', options[fi.frame] || '');
    });

    // Listener fallback: change no variant-picker
    var selectors = cfg().variantPickerSelectors || ['variant-picker'];
    var picker    = null;
    for (var i = 0; i < selectors.length; i++) {
      picker = document.querySelector(selectors[i]);
      if (picker) break;
    }

    if (picker) {
      var pointerdownHandled = false;

      // Listener imediato: pointerdown nas opções — aplica overlay antes do tema processar
      picker.addEventListener('pointerdown', function (e) {
        var label = e.target.closest('label.variant-option__button-label');
        if (!label) return;

        var input = label.querySelector('input[type="radio"]');
        if (!input) return;

        var fieldset = input.closest('fieldset[data-fieldset-index]');
        if (!fieldset) return;

        var fi    = cfg().optionIndex || { material: 1, frame: 2 };
        var idx   = parseInt(input.dataset.fieldsetIndex || fieldset.dataset.fieldsetIndex, 10);
        var value = input.value || '';

        if (idx !== fi.material && idx !== fi.frame) return;

        var current  = readVariantOptions();
        var material = current.material;
        var frame    = current.frame;

        if (idx === fi.material) material = value;
        if (idx === fi.frame)    frame    = value;

        var orientation = E.resolveOrientation(artworkEl, orientHint, cfg());
        if (!orientation) return;

        pointerdownHandled = true;
        log('pointerdown antecipado:', { idx: idx, value: value, material: material, frame: frame });
        applyComposition(artworkEl, null, orientation, material, frame, assetBaseUrl);
        overlayEl = container.querySelector('.pv-overlay');
      });

      // Fallback change — só executa se pointerdown não tratou
      picker.addEventListener('change', function () {
        if (fallbackTimer) clearTimeout(fallbackTimer);

        if (pointerdownHandled) {
          pointerdownHandled = false;
          return;
        }

        fallbackTimer = setTimeout(function () {
          fallbackTimer = null;
          var current = readVariantOptions();
          log('change fallback:', current);
          update(current.material, current.frame);
        }, 0);
      });
    }

    log('Product Visualizer pronto.');

    // Injeta botão "Ver na parede" após o buy-buttons se ainda não existir
    injectVerNaParede();
  }

  // ==========================================================================
  // BOTÃO VER NA PAREDE — injetado via JS após buy-buttons
  // ==========================================================================

  function injectVerNaParede() {
    if (document.querySelector('.lws-ver-na-parede')) return;

    var pvData      = window.ProductVisualizerData || {};
    var orientHint  = pvData.orientationHint || 'vertical';

    var productHandle = (window.location.pathname.match(/\/products\/([^/?]+)/) || [])[1] || '';
    if (!productHandle) return;

    var orientation = orientHint || 'vertical';

    function getVariantId() {
      var forms = document.querySelectorAll('form[action="/cart/add"]');
      for (var i = 0; i < forms.length; i++) {
        var input = forms[i].querySelector('input[name="id"]');
        if (input && input.value) return input.value;
      }
      return '';
    }

    var wrap = document.createElement('div');
    wrap.className = 'lws-ver-na-parede';

    var btn = document.createElement('button');
    btn.type      = 'button';
    btn.className = 'lws-ver-na-parede__btn';
    btn.setAttribute('data-action', 'ver-na-parede');
    btn.setAttribute('aria-label', 'Visualizar este quadro simulado em uma parede');
    btn.innerHTML =
      '<svg class="lws-ver-na-parede__icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<rect x="3" y="3" width="18" height="14" rx="1"/>' +
        '<path d="M3 19h18M8 19v2M16 19v2"/>' +
      '</svg>' +
      ' Ver na parede';

    btn.addEventListener('click', function () {
      var params = new URLSearchParams();
      params.set('product',     productHandle);
      params.set('variant',     getVariantId());
      params.set('orientation', orientation);
      window.location.href = '/pages/simulador?' + params.toString();
    });

    wrap.appendChild(btn);

    var anchor =
      document.querySelector('add-to-cart-component') ||
      document.querySelector('[data-block-type="buy-buttons"]') ||
      document.querySelector('buy-buttons') ||
      document.querySelector('form[action="/cart/add"][id*="BuyButtons"]') ||
      document.querySelector('form[action="/cart/add"]:not(.payment-terms)');

    if (anchor) {
      anchor.insertAdjacentElement('afterend', wrap);
      log('Ver na parede injetado após', anchor.tagName || anchor.className);
    } else {
      warn('Nenhum anchor encontrado para injetar Ver na parede');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();